/**
 * Utility functions for handling communication data in simulations
 */

/**
 * Extract recipient information from message data based on channel type
 * @param {Object} messageData - The message data object
 * @param {string} channelType - The communication channel type (e.g., 'email', 'sms', 'phone')
 * @returns {string} The recipient identifier
 */
export const getRecipient = (messageData, channelType) => {
  if (!messageData) return 'unknown';
  
  const channelTypeLower = String(channelType).toLowerCase();
  
  switch (channelTypeLower) {
    case 'email':
      return messageData.email || messageData.to || messageData.recipient || 'unknown@example.com';
    case 'sms':
    case 'phone':
      return messageData.phone || messageData.phoneNumber || messageData.to || messageData.recipient || 'unknown';
    case 'social':
      return messageData.username || messageData.handle || messageData.to || messageData.recipient || 'unknown';
    default:
      return messageData.recipient || messageData.to || messageData.email || messageData.phone || 'unknown';
  }
};

/**
 * Extract message content from message data
 * @param {Object} messageData - The message data object
 * @returns {string} The message content
 */
export const getMessageContent = (messageData) => {
  if (!messageData) return '';
  
  return messageData.content || 
         messageData.message || 
         messageData.body || 
         messageData.text || 
         messageData.description || 
         '';
};

/**
 * Get default subject based on channel type
 * @param {string} channelType - The communication channel type
 * @returns {string} Default subject for the channel type
 */
export const getDefaultSubject = (channelType) => {
  const channelTypeLower = String(channelType).toLowerCase();
  
  switch (channelTypeLower) {
    case 'email':
      return 'Important Security Alert';
    case 'sms':
      return 'Security Alert';
    case 'phone':
      return 'Security Call';
    case 'social':
      return 'Security Notification';
    case 'web':
      return 'Web Security Alert';
    default:
      return 'Security Communication';
  }
};

/**
 * Validate communication data for required fields
 * @param {Object} messageData - The message data object
 * @param {string} channelType - The communication channel type
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateCommunicationData = (messageData, channelType) => {
  const errors = [];
  
  if (!messageData) {
    errors.push('Message data is required');
    return { isValid: false, errors };
  }
  
  if (!channelType) {
    errors.push('Channel type is required');
  }
  
  const content = getMessageContent(messageData);
  if (!content || content.trim().length === 0) {
    errors.push('Message content is required');
  }
  
  const recipient = getRecipient(messageData, channelType);
  if (!recipient || recipient === 'unknown') {
    errors.push('Valid recipient is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format communication data for API submission
 * @param {Object} messageData - The message data object
 * @param {string} channelType - The communication channel type
 * @returns {Object} Formatted communication data
 */
export const formatCommunicationData = (messageData, channelType) => {
  if (!messageData) return {};
  
  return {
    recipient: getRecipient(messageData, channelType),
    content: getMessageContent(messageData),
    subject: messageData.subject || getDefaultSubject(channelType),
    channelType: String(channelType).toLowerCase(),
    timestamp: new Date().toISOString(),
    ...messageData // Include any additional properties
  };
};
